import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiUsers, FiStar, FiGift } = FiIcons;

const DonationTiers = () => {
  const tiers = [
    {
      amount: 500,
      title: "One Day of Dignity",
      description: "Shelter + Meals + Skills for 1 person",
      icon: FiHeart,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      impact: "1 life supported for 1 day",
      features: ["Safe shelter for 1 night", "3 nutritious meals", "Basic healthcare", "Skills assessment"]
    },
    {
      amount: 5000,
      title: "Transform 10 Lives",
      description: "Complete support for 10 people",
      icon: FiUsers,
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      impact: "10 lives supported for 1 day each",
      features: ["10 days of shelter", "30 meals provided", "Healthcare for 10", "Group counseling session"]
    },
    {
      amount: 50000,
      title: "Support Entire Shelter",
      description: "Fund a shelter for one month",
      icon: FiStar,
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50",
      textColor: "text-orange-600",
      impact: "Entire shelter operational for 1 month",
      features: ["Shelter operations", "Staff salaries", "Food supplies", "Medical care", "Skill training programs"]
    },
    {
      amount: 100000,
      title: "Patron's Circle",
      description: "Premium impact membership",
      icon: FiGift,
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      textColor: "text-purple-600",
      impact: "200+ lives transformed",
      features: ["Exclusive updates", "Site visits", "Annual report", "Tax benefits", "Recognition certificate"]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <SafeIcon icon={FiHeart} className="w-4 h-4" />
            <span>Choose Your Impact</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            How Your
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
              Contribution Helps
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Every rupee builds independence, not dependency. Choose the impact level that resonates with you.
          </p>
        </motion.div>

        {/* Donation Tiers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.amount}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              className={`bg-white rounded-2xl shadow-xl overflow-hidden border-2 ${
                index === 2 ? 'border-orange-200 ring-4 ring-orange-100' : 'border-gray-100'
              } relative`}
            >
              {/* Popular Badge */}
              {index === 2 && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="bg-orange-600 text-white px-6 py-2 rounded-full text-sm font-bold">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Icon */}
                <div className="relative mb-6">
                  <div className={`w-16 h-16 ${tier.bgColor} rounded-2xl flex items-center justify-center mx-auto`}>
                    <SafeIcon icon={tier.icon} className={`w-8 h-8 ${tier.textColor}`} />
                  </div>
                </div>

                {/* Amount */}
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-gray-900 mb-2">
                    ₹{tier.amount.toLocaleString()}
                  </div>
                  <div className="text-lg font-semibold text-gray-700 mb-2">{tier.title}</div>
                  <div className="text-gray-600">{tier.description}</div>
                </div>

                {/* Impact */}
                <div className={`bg-gradient-to-r ${tier.color} rounded-xl p-4 text-white text-center mb-6`}>
                  <div className="text-sm font-semibold mb-1">Your Impact</div>
                  <div className="text-xs">{tier.impact}</div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-8">
                  {tier.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-700">
                      <div className={`w-2 h-2 ${tier.textColor.replace('text-', 'bg-')} rounded-full mr-3 flex-shrink-0`}></div>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Donate Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                    index === 2
                      ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg hover:shadow-xl'
                      : `bg-gradient-to-r ${tier.color} text-white hover:shadow-lg`
                  }`}
                >
                  Donate ₹{tier.amount.toLocaleString()}
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Custom Amount Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto border-2 border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Choose Your Own Amount
            </h3>
            <p className="text-gray-600 mb-8">
              Every contribution, no matter the size, makes a meaningful difference in someone's life.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <div className="flex items-center space-x-2">
                <span className="text-2xl font-bold text-gray-700">₹</span>
                <input
                  type="number"
                  placeholder="Enter amount"
                  className="w-40 px-4 py-3 border-2 border-gray-300 rounded-xl text-center text-lg font-semibold focus:outline-none focus:border-orange-500 transition-colors duration-200"
                  min="100"
                />
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-8 py-3 rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300"
              >
                Donate Custom Amount
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">
              "Every rupee builds independence, not dependency."
            </h3>
            <p className="text-gray-300 text-lg">
              Your contribution doesn't just help for today – it creates sustainable change that lasts a lifetime.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DonationTiers;